/**
 * Client-side citation text matching against Textract LINE geometries.
 * Port of the server-side TextLocatorService LCS algorithm.
 */

import { normalize, lcsRatio } from './textMatchUtils';

interface LineGeometry {
  text: string;
  left: number;
  top: number;
  width: number;
  height: number;
}

interface BoundingBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

const MIN_MATCH_THRESHOLD = 0.4;

/**
 * Compute search-text coverage: what fraction of the search text is covered
 * by the longest common substring with the candidate window.
 */
function searchCoverage(normalizedSearch: string, candidate: string): number {
  const ratio = lcsRatio(normalizedSearch, candidate);
  const lcsLen = ratio * Math.min(normalizedSearch.length, candidate.length);
  return normalizedSearch.length > 0 ? lcsLen / normalizedSearch.length : 0;
}

/**
 * Word-overlap coverage: what fraction of significant search words (4+ chars)
 * appear in the candidate. Fast O(n) check that handles table/structured data
 * where matching content is interleaved with non-matching rows.
 */
function wordCoverage(searchWords: string[], candidate: string): number {
  if (searchWords.length === 0) return 0;
  let matched = 0;
  for (const word of searchWords) {
    if (candidate.includes(word)) matched++;
  }
  return matched / searchWords.length;
}

/**
 * Extract significant words (4+ chars) from normalised text for word-overlap matching.
 */
function extractSignificantWords(text: string): string[] {
  return text.split(/\s+/).filter(w => w.length >= 4);
}

/**
 * Match citation text against LINE geometries and return the union bounding box
 * of the best matching contiguous lines, or null if no match above threshold.
 *
 * Two strategies:
 * 1. LCS substring coverage — works well for prose/narrative text
 * 2. Word-overlap coverage — handles table/structured data where matching
 *    content is interleaved with non-matching rows from other columns
 */
export function matchCitationText(
  lines: LineGeometry[],
  searchText: string
): BoundingBox | null {
  if (!lines?.length || !searchText?.trim()) return null;

  const normalizedSearch = normalize(searchText);
  if (!normalizedSearch) return null;

  const searchWords = extractSignificantWords(normalizedSearch);

  let bestScore = 0;
  let bestStart = -1;
  let bestEnd = -1;

  for (let start = 0; start < lines.length; start++) {
    let combined = '';
    for (let end = start; end < Math.min(start + 40, lines.length); end++) {
      if (combined) combined += ' ';
      combined += normalize(lines[end].text);

      // LCS substring score (good for prose)
      const lcsCov = searchCoverage(normalizedSearch, combined);
      // Word overlap score (good for tables)
      const wordCov = wordCoverage(searchWords, combined);

      const score = Math.max(lcsCov, wordCov);
      if (score > bestScore) {
        bestScore = score;
        bestStart = start;
        bestEnd = end;
      }
      // Stop expanding once the candidate is much longer than the search text
      if (combined.length > normalizedSearch.length * 4) break;
      // Good enough — stop early
      if (score > 0.9) break;
    }
    if (bestScore > 0.9) break;
  }

  if (bestScore < MIN_MATCH_THRESHOLD || bestStart < 0) return null;

  // Refinement: shrink the window from both ends to find the tightest
  // sub-window that still covers at least 80% of the best score.
  if (bestStart >= 0 && bestEnd > bestStart) {
    // Shrink from the start
    for (let newStart = bestStart + 1; newStart <= bestEnd; newStart++) {
      let refined = '';
      for (let k = newStart; k <= bestEnd; k++) {
        if (refined) refined += ' ';
        refined += normalize(lines[k].text);
      }
      const lcsCov = searchCoverage(normalizedSearch, refined);
      const wordCov = wordCoverage(searchWords, refined);
      if (Math.max(lcsCov, wordCov) < bestScore * 0.8) break;
      bestStart = newStart;
    }
    // Shrink from the end
    for (let newEnd = bestEnd - 1; newEnd >= bestStart; newEnd--) {
      let refined = '';
      for (let k = bestStart; k <= newEnd; k++) {
        if (refined) refined += ' ';
        refined += normalize(lines[k].text);
      }
      const lcsCov = searchCoverage(normalizedSearch, refined);
      const wordCov = wordCoverage(searchWords, refined);
      if (Math.max(lcsCov, wordCov) < bestScore * 0.8) break;
      bestEnd = newEnd;
    }
  }

  let minLeft = Infinity;
  let minTop = Infinity;
  let maxRight = 0;
  let maxBottom = 0;

  for (let i = bestStart; i <= bestEnd; i++) {
    const line = lines[i];
    minLeft = Math.min(minLeft, line.left);
    minTop = Math.min(minTop, line.top);
    maxRight = Math.max(maxRight, line.left + line.width);
    maxBottom = Math.max(maxBottom, line.top + line.height);
  }

  return {
    x: minLeft,
    y: minTop,
    width: maxRight - minLeft,
    height: maxBottom - minTop,
  };
}
